import { defineStore } from "pinia";
import { ref, type Ref } from "vue";
import type { ApiResponse, PaginatedResponse, Pagination } from "@/app/types";
import api from "@/app/services/apiNir.ts";
import type { AxiosResponse } from "axios";
import {
    ApplicationQuery,
    ApplicationStats,
    BulkApplicationRow, BulkApplicationStats,
    BulkEntityApplication, DataUpdateApplication, DataUpdateTask,
    EntityApplication,
    EntityTask, ForcedEntityApplication
} from "@/domains/home/services/nir/registration/types";

export const useNIRApplicationsStore = defineStore("nir-applications", () => {
  const okResponse:Ref<object|undefined> = ref()
  const fileBytes:Ref<string|undefined> = ref()
  const entityResponse:Ref<{id:string}|undefined> = ref()
  const pagination:Ref<Pagination|undefined> = ref()
  const applications:Ref<EntityApplication[]|undefined> = ref()
  const forcedApplications:Ref<ForcedEntityApplication[]|undefined> = ref()
  const dataUpdateApplications:Ref<DataUpdateApplication[]|undefined> = ref()
  const bulkApplications:Ref<BulkEntityApplication[]|undefined> = ref()
  const bulkApplicationRows:Ref<BulkApplicationRow[]|undefined> = ref()
  const applicationQueries: Ref<ApplicationQuery[] | undefined> = ref();
  const task:Ref<EntityTask|undefined> = ref()
  const dataUpdateTask:Ref<DataUpdateTask|undefined> = ref()
  const vtask:Ref<EntityTask|undefined> = ref()
  const taskData:Ref<EntityTask|undefined> = ref()
  const stats:Ref<ApplicationStats|undefined> = ref()
  const bulkStats:Ref<BulkApplicationStats|undefined> = ref()
  const bulkApplicationStats:Ref<BulkApplicationStats|undefined> = ref()

  const pushData = async (payload:object) => {
    return api.post("/v1/applications/push", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const addEntity = async (payload:object) => {
    return api.post("/v1/applications/step", payload)
      .then((response: AxiosResponse<ApiResponse<{id:string}>>) => {
          entityResponse.value = response.data.data
      })
  }

    const submit = async (payload:object) => {
        return api.post("/v1/applications/submit", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    const uploadEntityFile = async (payload:object) => {
        return api.post("/v1/applications/attach-file", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    const removeEntityFile = async (payload:object) => {
        return api.put("/v1/applications/remove-file", payload)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

  const confirmAddedData = async (payload:object) => {
    return api.post("/v1/applications/confirm", payload)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data
      })
  }

  const assign = async (payload:object) => {
    return api.post("/v1/applications/assign", payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const approve = async (payload:object) => {
    return api.post("/v1/applications/approve", payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

  const reject = async (payload:object) => {
    return api.post("/v1/applications/approve", payload)
        .then((response: AxiosResponse<ApiResponse<object>>) => {
          okResponse.value = response.data.data
        })
  }

    const getFile = async (payload:object) => {
        return api.post("/v1/applications/attachment", payload)
            .then((response: AxiosResponse<ApiResponse<{data:string}>>) => {
                fileBytes.value = response.data.data.data
            })
    }

  const fetchApplications = async (trackingNumber:string, name:string, typeId:string, subTypeId:string, institutionId:string, status:string, page:number, limit:number) => {
    let path = `/v1/applications?page=${page}&limit=${limit}`
      if (trackingNumber){
          path += `&trackingNumber=${trackingNumber}`
      }
      if(name){
          path += `&name=${name}`
      }
      if (typeId) {
          path += `&typeId=${typeId}`
      }
      if (subTypeId) {
          path += `&subTypeId=${subTypeId}`
      }
      if (status) {
          path += `&status=${status}`
      }
      if (institutionId) {
          path += `&institutionId=${institutionId}`
      }
    return api.get(path)
        .then((response: AxiosResponse<ApiResponse<PaginatedResponse<EntityApplication>>>) => {
          applications.value = response.data.data.content
          pagination.value = response.data.data.pagination
        })
  }

    const fetchForcedApplications = async (
        trackingNumber: string,
        name: string,
        institutionId:string,
        typeId: string,
        subTypeId: string,
        status: string,
        page: number,
        limit: number,
    ) => {
        let path = `/v1/applications/forced?page=${page}&limit=${limit}`
        if (trackingNumber) {
            path += `&trackingNumber=${trackingNumber}`
        }
        if (name) {
            path += `&name=${name}`
        }
        if (institutionId) {
            path += `&institutionId=${institutionId}`
        }
        if (typeId) {
            path += `&typeId=${typeId}`
        }
        if (subTypeId) {
            path += `&subTypeId=${subTypeId}`
        }
        if (status) {
            path += `&status=${status}`
        }
        return api
            .get(path)
            .then((response: AxiosResponse<ApiResponse<PaginatedResponse<ForcedEntityApplication>>>) => {
                forcedApplications.value = response.data.data.content
                pagination.value = response.data.data.pagination
            })
    }

    const fetchDataUpdateApplications = async (
        trackingNumber: string,
        ern: string,
        institutionId:string,
        typeId: string,
        subTypeId: string,
        status: string,
        page: number,
        limit: number) => {

        let path = `/v1/data-updates?page=${page}&limit=${limit}`
        if (ern) {
            path += `&ern=${ern}`
        }
        if (institutionId) {
            path += `&institutionId=${institutionId}`
        }
        if (typeId) {
            path += `&typeId=${typeId}`
        }
        if (subTypeId) {
            path += `&subTypeId=${subTypeId}`
        }
        if (status) {
            path += `&status=${status}`
        }
        if (trackingNumber) {
            path += `&trackingNumber=${trackingNumber}`
        }

        return api
            .get(path)
            .then((response: AxiosResponse<ApiResponse<PaginatedResponse<DataUpdateApplication>>>) => {
                dataUpdateApplications.value = response.data.data.content
                pagination.value = response.data.data.pagination
            })
    }

    const fetchAmendmentApplications = async (
        trackingNumber: string,
        ern: string,
        institutionId:string,
        typeId: string,
        subTypeId: string,
        status: string,
        page: number,
        limit: number) => {

        let path = `/v1/amendments?page=${page}&limit=${limit}`
        if (ern) {
            path += `&ern=${ern}`
        }
        if (institutionId) {
            path += `&institutionId=${institutionId}`
        }
        if (typeId) {
            path += `&typeId=${typeId}`
        }
        if (subTypeId) {
            path += `&subTypeId=${subTypeId}`
        }
        if (status) {
            path += `&status=${status}`
        }
        if (trackingNumber) {
            path += `&trackingNumber=${trackingNumber}`
        }

        return api
            .get(path)
            .then((response: AxiosResponse<ApiResponse<PaginatedResponse<DataUpdateApplication>>>) => {
                dataUpdateApplications.value = response.data.data.content
                pagination.value = response.data.data.pagination
            })
    }


    const fetchBulkApplications = async (page:number, limit:number) => {
        let path = `/v1/applications/bulk?page=${page}&limit=${limit}`
        return api.get(path)
            .then((response: AxiosResponse<ApiResponse<PaginatedResponse<BulkEntityApplication>>>) => {
                bulkApplications.value = response.data.data.content
                pagination.value = response.data.data.pagination
            })
    }

    const fetchBulkApplicationRows = async (id:string, search:string, status:string, page:number, limit:number) => {
        let path = `/v1/applications/bulk/rows?id=${id}&page=${page}&limit=${limit}`
        if (search) {
            path += `&search=${search}`
        }
        if (status) {
            path += `&status=${status}`
        }
        return api.get(path)
            .then((response: AxiosResponse<ApiResponse<PaginatedResponse<BulkApplicationRow>>>) => {
                bulkApplicationRows.value = response.data.data.content
                pagination.value = response.data.data.pagination
            })
    }

  const getNextTask = async (userId:string) => {
    let path = `/v1/applications/next?userId=${userId}`
    return api.get(path)
        .then((response: AxiosResponse<ApiResponse<EntityTask>>) => {
          task.value = response.data.data
        })
  }

  const fetchApplicationQueries = async (id: string) => {
    let path = `/v1/applications/task/queries?id=${id}`;
    return api.get(path)
      .then((response: AxiosResponse<ApiResponse<ApplicationQuery[]>>) => {
        applicationQueries.value = response.data.data;
      });
  };

  const sendQueryResponse = async (payload: object) => {
    let path = `/v1/applications/task/queries`;
    return api.post(path)
      .then((response: AxiosResponse<ApiResponse<object>>) => {
        okResponse.value = response.data.data;
      });
  };

  const fetchTask = async (id:string) => {
    let path = `/v1/applications/task?id=${id}`
    return api.get(path)
            .then((response: AxiosResponse<ApiResponse<EntityTask>>) => {
                vtask.value = response.data.data
            })
  }

    const fetchDataUpdateTask = async (id: string) => {
        return api
            .get(`/v1/data-updates/task?id=${id}`)
            .then((response: AxiosResponse<ApiResponse<DataUpdateTask>>) => {
                dataUpdateTask.value = response.data.data
            })
    }

    const fetchAmendmentTask = async (id: string) => {
        return api
            .get(`/v1/amendments/task?id=${id}`)
            .then((response: AxiosResponse<ApiResponse<DataUpdateTask>>) => {
                dataUpdateTask.value = response.data.data
            })
    }

  const fetchStatistics = async () => {
    let path = `/v1/applications/stats`
    return api.get(path)
        .then((response: AxiosResponse<ApiResponse<ApplicationStats>>) => {
          stats.value = response.data.data
        })
  }

    const fetchDataUpdateStatistics = async () => {
        let path = `/v1/data-updates/stats`
        return api.get(path)
            .then((response: AxiosResponse<ApiResponse<ApplicationStats>>) => {
                stats.value = response.data.data
            })
    }

    const fetchAmendmentStatistics = async () => {
        let path = `/v1/amendments/stats`
        return api.get(path)
            .then((response: AxiosResponse<ApiResponse<ApplicationStats>>) => {
                stats.value = response.data.data
            })
    }

    const fetchBulkStatistics = async () => {
        let path = `/v1/applications/bulk/stats`
        return api.get(path)
            .then((response: AxiosResponse<ApiResponse<BulkApplicationStats>>) => {
                bulkStats.value = response.data.data
            })
    }

    const fetchBulkApplicationStatistics = async (id:string) => {
        let path = `/v1/applications/bulk/rows/stats?id=${id}`
        return api.get(path)
            .then((response: AxiosResponse<ApiResponse<BulkApplicationStats>>) => {
                bulkApplicationStats.value = response.data.data
            })
    }

    const getExportFailedBulkApplications = async (id:string) => {
        let path = `/v1/applications/bulk/export-failed-rows?id=${id}`
        return api.get(path)
            .then((response: AxiosResponse<ApiResponse<object>>) => {
                okResponse.value = response.data.data
            })
    }

    const fetchTaskData = async (id:string) => {
        let path = `/v1/applications/task-data?id=${id}`
        return api.get(path)
            .then((response: AxiosResponse<ApiResponse<EntityTask>>) => {
                taskData.value = response.data.data
            })
    }

  return{
      okResponse,
      entityResponse,
      pagination,
      applications,
      forcedApplications,
      bulkApplications,
      bulkApplicationRows,
      task,
      vtask,
      dataUpdateTask,
      stats,
      bulkStats,
      bulkApplicationStats,
      fileBytes,
      taskData,
    applicationQueries,
      dataUpdateApplications,
      pushData,
      addEntity,
      confirmAddedData,
      fetchApplications,
      getNextTask,
      fetchStatistics,
      fetchBulkStatistics,
      fetchTask,
      assign,
      approve,
      reject,
      uploadEntityFile,
      fetchBulkApplications,
      fetchBulkApplicationRows,
      fetchBulkApplicationStatistics,
      getFile,
      removeEntityFile,
      submit,
    fetchTaskData,
    fetchApplicationQueries,
    sendQueryResponse,
      getExportFailedBulkApplications,
      fetchForcedApplications,
      fetchDataUpdateApplications,
      fetchDataUpdateTask,
      fetchDataUpdateStatistics,
      fetchAmendmentStatistics,
      fetchAmendmentApplications,
      fetchAmendmentTask
  }
})